import React, { useState, useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'

import { openPortalModal } from 'src/widgets/embed/openPortalModal'
import { injectLocalPortalModal } from '../injectLocalPortalModal'

// Searchable list-picker modal — crypto-currency picker + fiat-currency
// picker share this component. The trigger button (rendered via the
// `render` prop) calls openPortalModal, which routes to one of three
// runtime paths (standalone in-page, iframe-at-host-page when properly
// embedded, in-page fallback when improperly embedded) and resolves
// with a DOM target + close fn. We portal the modal-header (title +
// close + search) + modal-body (scrollable radio list) into that
// target. The picker UI is identical across all three modes — only
// the surrounding chrome changes.
//
// See openPortalModal.js + project_interactive_modal_pipeline memory
// ("Portal-mount modals") for the broader pattern.

const optionLabel = option => `${option.label} (${option.value})`

const FilterList = ({
  id, title, options, value, onChange, blank_modal_src, render,
}) => {
  const [ portal, setPortal ] = useState(null)
  const [ filter, setFilter ] = useState('')
  const [ pending, setPending ] = useState(value)
  const wasTrulyClicked = useRef()

  const filteredOptions = useMemo(() => options.filter(option =>
    optionLabel(option).toLowerCase().indexOf(filter.toLowerCase()) > -1
  ), [ filter, options ])

  const open = async () => {
    setPending(value)
    setFilter('')
    const result = await openPortalModal({
      id,
      src: blank_modal_src,
      ariaLabel: title,
      maxWidth: '30rem',
      scrollWithin: 'both',
      renderLocal: injectLocalPortalModal,
    })
    setPortal(result)
  }

  const selectValueAndClose = () => {
    onChange(pending)
    portal?.close()
  }

  const handleFilterChange = e => setFilter(e.target.value)
  const handleOptionChange = e => setPending(e.target.value)

  const handleMouseUp = () => {
    wasTrulyClicked.current = true
    setTimeout(() => wasTrulyClicked.current = false, 10)
  }

  const handleFilterKeyDown = e => {
    switch (e.key) {
      case 'ArrowUp': {
        e.preventDefault()
        const currentIndex = filteredOptions.findIndex(o => o.value == pending)
        const previousOption = filteredOptions[currentIndex - 1]
        if (previousOption) setPending(previousOption.value)
        break
      }
      case 'ArrowDown': {
        e.preventDefault()
        const currentIndex = filteredOptions.findIndex(o => o.value == pending)
        const nextOption = filteredOptions[currentIndex + 1]
        if (nextOption) setPending(nextOption.value)
        break
      }
      case 'Enter':
        selectValueAndClose()
    }
  }

  const handleOptionKeyDown = e => {
    switch (e.key) {
      case 'ArrowUp':
        if (e.target.value == filteredOptions[0].value)
          e.preventDefault()
        break
      case 'ArrowDown':
        if (e.target.value == filteredOptions[filteredOptions.length - 1].value)
          e.preventDefault()
        break
      case 'Enter':
      case ' ':
        selectValueAndClose()
    }
  }

  // Auto-confirm on a *real* mouse click on a radio: handleMouseUp
  // sets the flag during the mouseup phase, then if `pending` then
  // changes (which the radio's onChange triggers), we close. Keyboard
  // navigation also fires onChange but with no preceding mouseup →
  // keyboard moves don't auto-close, matching pre-refactor behavior.
  // Also scrolls the selected option into view so arrow-key
  // navigation past the visible window keeps the cursor visible.
  // portal.target is the modal-content (mode 1/3) or the iframe body
  // (mode 2) — both contain the radio inputs after our portal mount.
  useEffect(() => {
    if (!portal?.target) return
    portal.target.querySelector(':checked')
      ?.parentNode.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      })
    if (wasTrulyClicked.current) selectValueAndClose()
  }, [ pending, portal ])

  return (
    <>
      {render({ open })}
      {portal?.target && createPortal(
        <>
          <div className="modal-header flex-wrap position-sticky top-0 bg-body p-3 border-bottom z-3">
            <h5 className="modal-title">{title}</h5>
            <button
              type="button"
              className="btn-close hide-in-blank"
              aria-label="Close"
              onClick={() => portal.close()}
            />
            <div className="w-100 mt-3">
              <div className="position-relative">
                <input
                  type="search"
                  className="form-control"
                  style={{ paddingLeft: '2.375rem' }}
                  value={filter}
                  onChange={handleFilterChange}
                  onKeyDown={handleFilterKeyDown}
                  autoFocus
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute"
                  style={{ top: '0.75rem', left: '0.75rem', pointerEvents: 'none' }}
                />
              </div>
            </div>
          </div>
          <div className="modal-body px-0 py-3" onMouseUp={handleMouseUp}>
            {filteredOptions.map(option => {
              const optionId = `${id}Option${option.value}`
              const selected = option.value == pending

              return (
                <div className="d-grid" key={option.value} style={{ scrollMargin: '1rem' }}>
                  <input
                    type="radio"
                    className="btn-check"
                    id={optionId}
                    name={id}
                    checked={selected}
                    value={option.value}
                    onChange={handleOptionChange}
                    onKeyDown={handleOptionKeyDown}
                  />
                  <label
                    className="btn btn-dropdown-item text-truncate text-start rounded-0 border-0 px-3"
                    htmlFor={optionId}
                  >{optionLabel(option)}</label>
                </div>
              )
            })}
          </div>
        </>,
        portal.target
      )}
    </>
  )
}

export default FilterList
